import { parse } from 'query-string';
import { API_KEYS, LOCAL_STORAGE, LOGGED_OUT_AND_SIDEBAR_URLS } from './constants';
import { createBasicInfo, hasApplicationFeature, isImpersonator } from './utils';
import tokenManager from './tokenManager';
import { history } from './Helpers/history';

const {
    BASIC_INFO,
    REDIRECT_URL,
    OIDC_SILENT_LOGIN_ATTEMPTED,
    OIDC_INTERACTIVE_LOGIN_ATTEMPTED,
} = LOCAL_STORAGE;

export const KEYCLOAK_OIDC_FEATURE = 'keycloak-oidc';

/**
 * The interactive-attempt marker is the /login shim's loop-breaker: while it is set, a return to
 * /login renders the retry state instead of redirecting again, so a failing exchange or callback
 * can never bounce the browser between the SPA and Keycloak. Only a successful exchange or a
 * deliberate sign-out clears it.
 */
export const hasAttemptedInteractiveLogin = () =>
    !!sessionStorage.getItem(OIDC_INTERACTIVE_LOGIN_ATTEMPTED);

/**
 * For a sign-out the user chose: the attempt that set these guards is over, so the next /login
 * should go to Keycloak rather than park on the retry state.
 */
export const clearLoginAttempts = () => {
    sessionStorage.removeItem(OIDC_SILENT_LOGIN_ATTEMPTED);
    sessionStorage.removeItem(OIDC_INTERACTIVE_LOGIN_ATTEMPTED);
};

export const redirectToInteractiveLogin = () => {
    sessionStorage.setItem(OIDC_INTERACTIVE_LOGIN_ATTEMPTED, '1');

    window.location.href = `${API_KEYS.api_url}/auth/oidc/login`;
};

/**
 * Never settles. Returned when the bootstrap has started a top-level navigation away from the
 * app, so the caller does not render a doomed frame while the browser unloads the page.
 */
const navigationInProgress = () => new Promise(() => {});

const isLoggedOutUrl = pathname =>
    new RegExp('(' + LOGGED_OUT_AND_SIDEBAR_URLS.join('|') + ')').test(pathname);

/**
 * Swaps the single-use handoff code for a token pair. Unauthenticated by design - the code in the
 * body is the credential - and a bare fetch because the dataProvider does not exist yet.
 */
const exchangeCode = code =>
    fetch(`${API_KEYS.api_url}/auth/exchange`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
    }).then(response => {
        if (!response.ok) {
            // 401 (spent/unknown code) and 429 both end the same way: no session, interactive
            // login. The code is single-use so there is nothing to retry.
            throw new Error('Auth code exchange failed');
        }

        return response.json();
    });

/**
 * Populates BASIC_INFO before the first render, exactly as the password and lexiktoken login
 * paths do. Without this, `getPermissions` sees empty permissions and bounces the freshly
 * authenticated user straight back to /login.
 *
 * Mirrors the dataProvider's GET_ONE response shape (`{ data: { ...json.data, id } }`) because
 * the dataProvider is not constructed until the app renders.
 */
const prefetchBasicInfo = () =>
    fetch(`${API_KEYS.api_url}/professional/basic-info`, {
        headers: {
            Accept: 'application/json',
            'vf-token': tokenManager.getLoggedInToken(),
        },
    })
        .then(response => {
            if (!response.ok) {
                throw new Error('basic-info prefetch failed');
            }

            return response.json();
        })
        .then(json => {
            const response = { data: { ...json.data, id: json.data?.id || 'no-id' } };

            localStorage.setItem(BASIC_INFO, JSON.stringify(createBasicInfo(response)));
        });

/**
 * The prefetch, with one retry and never a rejection.
 *
 * It runs *after* the code has been exchanged and the tokens stored, so by then the session is
 * real and a failure here is a missing convenience, not a failed login. A transient blip on
 * `/professional/basic-info` must not cost the caller that session: the handoff code is
 * single-use and already spent, so there would be nothing left to retry with. When both attempts
 * fail, `getPermissions` routes on the empty BASIC_INFO exactly as it does for any other cold
 * start, and the tokens stay put.
 */
const prefetchBasicInfoTolerantly = () =>
    prefetchBasicInfo()
        .catch(() => prefetchBasicInfo())
        .catch(() => {});

/**
 * Replaces the current history entry, keeping the address bar free of one-time credentials.
 * The `?code` value must never survive into history, bookmarks or referrer headers.
 *
 * Goes through the app's shared history instance, not raw `replaceState`: that instance is
 * created at module load - before this bootstrap rewrites the URL - and react-admin renders
 * whatever location it captured. A raw `replaceState` fixes the address bar but leaves the
 * router mounting the stale route (e.g. the dashboard for `/?code=...`), whose data calls
 * then 401 as unhandled rejections on the failure path.
 */
const replaceUrl = url => history.replace(url);

const scrubbedCurrentUrl = () => {
    const url = new URL(window.location.href);

    url.searchParams.delete('code');
    url.searchParams.delete('login_required');

    return url.pathname + url.search + url.hash;
};

/**
 * OIDC bootstrap state machine. Runs once per cold load, before React renders, reading the raw
 * URL so nothing has had a chance to consume or rewrite it.
 *
 * Order is load-bearing:
 *   0. `?lexiktoken` - the admin "sign in as" / impersonation entry. It arrives with no stored
 *      token, so any later step would misread it as "truly logged out" and bounce the admin to
 *      Keycloak as themselves. Yield to the existing reader and skip OIDC entirely.
 *   1. `?code` - the handoff from a successful (silent or interactive) Keycloak login. Exchange
 *      it, store the pair on the existing token seam, scrub the URL, restore where the user was.
 *   2. `?login_required=1` - a silent attempt found no Keycloak session. Remember that in the
 *      loop guard and fall through to the interactive login.
 *   3. Truly logged out (no access and no refresh token) - redirect to the silent login, at most
 *      once per tab, never for impersonators and never from logged-out pages like /login.
 *   4. Anything else - the app behaves exactly as it does today.
 *
 * @returns {Promise<void>} settles when the app should render; never settles when the bootstrap
 * has navigated away.
 */
export const runOidcBootstrap = async () => {
    const query = parse(window.location.search);

    // Step 0: impersonation entry wins outright. The existing reader in
    // `authProvider.getPermissions` consumes it after render.
    if (query.lexiktoken) {
        return;
    }

    // Step 1: consume the one-time handoff code.
    if (query.code) {
        let exchanged;

        // Only the exchange may tear the session down. Everything after it runs with tokens
        // already stored, and tearing those down would discard a session the api has just
        // issued against a single-use code - unrecoverable without a fresh Keycloak round-trip.
        try {
            exchanged = await exchangeCode(query.code);
        } catch (e) {
            // Read storage now rather than trusting what was here on entry: a sibling tab may
            // have completed its own exchange while ours was in flight, and a stray `?code` can
            // land on a tab that already holds a working session. Either way the session here is
            // not one this failure created, so tearing it down would log the user out over a
            // spent URL param. The session is live either way, so no login attempt is still
            // outstanding: clear both loop guards, or a later visit to /login in this tab parks
            // on the retry line instead of going to Keycloak.
            if (tokenManager.getLoggedInToken() || tokenManager.getRefreshToken()) {
                sessionStorage.removeItem(OIDC_SILENT_LOGIN_ATTEMPTED);
                sessionStorage.removeItem(OIDC_INTERACTIVE_LOGIN_ATTEMPTED);
                replaceUrl(scrubbedCurrentUrl());

                return;
            }

            // No session came out of this. Guard against an immediate silent re-bounce and land
            // on the interactive login instead.
            tokenManager.clearTokens();
            localStorage.removeItem(BASIC_INFO);
            sessionStorage.setItem(OIDC_SILENT_LOGIN_ATTEMPTED, '1');
            replaceUrl('/login');

            return;
        }

        const { accessToken, refreshToken } = exchanged;

        // The exchange response always carries a refresh token; pass it explicitly because
        // `setTokens` treats an absent one as "keep whatever is stored".
        tokenManager.setTokens({ token: accessToken, refreshToken });

        await prefetchBasicInfoTolerantly();

        sessionStorage.removeItem(OIDC_SILENT_LOGIN_ATTEMPTED);
        sessionStorage.removeItem(OIDC_INTERACTIVE_LOGIN_ATTEMPTED);

        const returnUrl = sessionStorage.getItem(REDIRECT_URL);

        if (returnUrl) {
            sessionStorage.removeItem(REDIRECT_URL);
            replaceUrl(returnUrl);
        } else {
            replaceUrl(scrubbedCurrentUrl());
        }

        return;
    }

    // Step 2: a silent attempt already failed upstream - go interactive, do not bounce again.
    if (query.login_required) {
        sessionStorage.setItem(OIDC_SILENT_LOGIN_ATTEMPTED, '1');
        replaceUrl('/login');

        return;
    }

    // Step 3: truly logged out on a protected page - try the silent login, once per tab.
    if (
        hasApplicationFeature(KEYCLOAK_OIDC_FEATURE) &&
        !tokenManager.getLoggedInToken() &&
        !tokenManager.getRefreshToken() &&
        !isImpersonator() &&
        !sessionStorage.getItem(OIDC_SILENT_LOGIN_ATTEMPTED) &&
        !isLoggedOutUrl(window.location.pathname)
    ) {
        const { pathname, search } = window.location;

        sessionStorage.setItem(REDIRECT_URL, pathname + search);
        sessionStorage.setItem(OIDC_SILENT_LOGIN_ATTEMPTED, '1');

        window.location.href = `${API_KEYS.api_url}/auth/oidc/login?silent=1`;

        return navigationInProgress();
    }

    // Step 4: proceed as today.
};
